import QuickViewWorkOrder from "components/QuickViewWorkOrder/QuickViewWorkOrder";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useCallback, useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import JobBoardPdf from "components/Pdfs/JobBoardProductPdfPrintButton/JobBoardPdf";
import JobBoardExcel from "components/Pdfs/JobBoardProductPdfPrintButton/JobBoardExcel";
import { useSearchParams } from "react-router-dom";
import { convertSqlTimestampToDate, truncateString } from "utils/UtilityFuncs";
import JobBoardFullViewData from "components/DisplayBoard/JobBoardFullViewData";

/**
 * One clickable column header.
 *
 * Cycles ascending -> descending -> unsorted, so a third click puts the card
 * back to its natural order rather than trapping you in a sort you did not
 * want. The arrow only shows on the active column; the inactive ones stay
 * plain so the header does not turn into a row of arrows.
 */
function SortableHeader({ className, label, column, sort, onSort }) {
  const active = sort.by === column;

  return (
    <div
      className={`${className} user-select-none`}
      role="button"
      tabIndex={0}
      title={`Sort by ${label}`}
      onClick={() => onSort(column)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSort(column);
        }
      }}
      style={{ cursor: "pointer" }}
    >
      {label}
      {active && (
        <i
          className={`mdi ${
            sort.dir === "asc" ? "mdi-arrow-up" : "mdi-arrow-down"
          } ms-1`}
        />
      )}
    </div>
  );
}

export default function JobBoardItem({
  selectedLocation,
  woStatus = null,
  disableInvoiceReady = true,
  API_CALL,
  filterText,
  refreshStatusList,
  setRefreshStatusList,
  productType,
  dateFrom,
  dateTo,
}) {
  /*   All States
   ********************************************* */
  const [, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();

  const [showProductsModal, setShowProductsModal] = useState(false);

  const [openFullView, setOpenFullView] = useState(false);

  const [WOData, setWOData] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Sort is per CARD, not per board. Each card is its own table with its own
  // paging, so sorting Transfer Printing has no business reordering Embroidery.
  const [sort, setSort] = useState({ by: null, dir: "asc" });

  const [loading, setLoading] = useState(false);

  /*   All Functions
   ********************************************* */
  const loadingOn = () => {
    setLoading(true);
  };

  const loadingOff = () => {
    setLoading(false);
  };

  // asc -> desc -> off. Sorting is done by the API, not here: the card holds
  // one page of ten behind a Load More button, so sorting the loaded rows would
  // order that page and quietly leave the rest of the card out of it.
  const onSort = (column) => {
    setSort((prev) => {
      if (prev.by !== column) return { by: column, dir: "asc" };
      if (prev.dir === "asc") return { by: column, dir: "desc" };
      return { by: null, dir: "asc" };
    });
  };

  const updateSearchParams = (key, value) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);

      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }

      return newParams.toString();
    });
  };

  const GetJobBoardData = useCallback(
    async ({ pageNo = 1, reset = true } = {}) => {
      try {
        loadingOn();

        const searchQuery = filterText?.trim();
        let body = {
          query: searchQuery || undefined,
          productTypeId: productType?.productTypeId || undefined,
          locationId: selectedLocation || undefined,
          sortBy: sort.by || undefined,
          sortDir: sort.by ? sort.dir : undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
        };

        if (woStatus) {
          body.WOStatus = woStatus;
        }

        const { data } = await API.post(
          `${API_CALL}?page=${pageNo}&limit=${limit}`,
          body,
          { headers: authHeader() },
        );

        if (data?.success) {
          const incomingData = data?.data || [];

          setWOData((prev) =>
            reset ? incomingData : [...(prev || []), ...incomingData],
          );

          setPage(data?.page || pageNo);
          setTotalPages(data?.totalPages || 1);
          setTotalRecords(data?.total || 0);
          setRefresh(false);
          loadingOff();
          return;
        }

        // backward compatibility for older response shape
        if (data?.type === "success") {
          setWOData(data?.result || []);
          setPage(1);
          setTotalPages(1);
          setTotalRecords((data?.result || []).length);
          setRefresh(false);
          loadingOff();
          return;
        }

        if (data.type === "error") {
          dispatch(
            setNotification({
              message: data.message,
              type: "error",
            }),
          );
          setRefresh(false);
          loadingOff();
          return;
        }
        loadingOff();
        console.log(data);
      } catch (err) {
        console.log(err.message);
        dispatch(
          setNotification({
            message: err.message,
            type: "error",
          }),
        );
        loadingOff();
        setRefresh(false);
      }
    },
    [
      dispatch,
      filterText,
      woStatus,
      limit,
      selectedLocation,
      productType?.productTypeId,
      // Changing the sort or the date range rebuilds this callback, which the
      // effect below already watches -- so both refetch from page 1 rather
      // than appending a differently-ordered page onto the rows on screen.
      sort.by,
      sort.dir,
      dateFrom,
      dateTo,
    ],
  );

  const onLoadMore = () => {
    if (loading || page >= totalPages) {
      return;
    }

    GetJobBoardData({ pageNo: page + 1, reset: false });
  };

  /*   All UseEffects
   ********************************************* */

  useEffect(() => {
    if (refresh) {
      GetJobBoardData({ pageNo: 1, reset: true });
    }
  }, [refresh, GetJobBoardData]);

  useEffect(() => {
    let timeout = setTimeout(() => {
      GetJobBoardData({ pageNo: 1, reset: true });
    }, 100);
    return () => clearTimeout(timeout);
  }, [filterText, GetJobBoardData]);

  useEffect(() => {
    const productTypeKey = productType?.productTypes;
    if (productTypeKey && refreshStatusList?.[productTypeKey]) {
      GetJobBoardData({ pageNo: 1, reset: true });
      setRefreshStatusList &&
        setRefreshStatusList((prev) => ({
          ...prev,
          [productTypeKey]: false,
        }));
    }
  }, [
    refreshStatusList,
    productType?.productTypes,
    GetJobBoardData,
    setRefreshStatusList,
  ]);

  const hasMore = page < totalPages;

  return (
    <div>
      {loading && <Spinner />}
      {!WOData && <Spinner />}
      {/* ******************** FilterBy Header ************************* */}
      <div className="topHeader bg-primary text-white custom-border-radius">
        <div>{productType?.productTypes}</div>
        <div className="d-flex align-items-center gap-1">
          <button
            onClick={() => {
              setOpenFullView(true);
            }}
            className="btn btn-sm btn-success"
          >
            Open
          </button>
          {WOData && WOData?.length ? (
            <>
              <JobBoardPdf
                filterBy={productType}
                workOrderList={WOData}
                API_CALL={API_CALL}
                filterText={filterText}
                selectedLocation={selectedLocation}
                dateFrom={dateFrom}
                dateTo={dateTo}
                sortBy={sort.by}
                sortDir={sort.dir}
              />
              <JobBoardExcel
                filterBy={productType}
                API_CALL={API_CALL}
                filterText={filterText}
                selectedLocation={selectedLocation}
                dateFrom={dateFrom}
                dateTo={dateTo}
                sortBy={sort.by}
                sortDir={sort.dir}
                woStatus={woStatus}
              />
            </>
          ) : (
            <div></div>
          )}
          <button
            onClick={() => {
              setWOData(null);
              setRefresh(true);
            }}
            className="btn btn-sm btn-info"
          >
            Refresh
          </button>

          {totalRecords || 0}
        </div>
      </div>
      <div className="bodyHeader row">
        <SortableHeader
          className="col-2"
          label="W-ID"
          column="woId"
          sort={sort}
          onSort={onSort}
        />
        <SortableHeader
          className="col-4"
          label="Job Name"
          column="jobName"
          sort={sort}
          onSort={onSort}
        />
        <SortableHeader
          className="col-4"
          label="Customer Name"
          column="customerName"
          sort={sort}
          onSort={onSort}
        />
        <SortableHeader
          className="col-2 text-center"
          label="Due Date"
          column="WoDueDate"
          sort={sort}
          onSort={onSort}
        />
        {/* <div className="col-1 text-end">Qty</div> */}
      </div>
      <div
        className="bodyContent"
        style={{ height: "35vh", overflowY: "auto", overflowX: "hidden" }}
      >
        {WOData?.map((item, i) => (
          <div
            onDoubleClick={() => {
              setShowProductsModal(true);
              updateSearchParams("selected-work-order", item?.woId);
            }}
            key={item.id}
            className="row item"
          >
            <div className="col-2 px-0 text-nowrap">W-{item?.woId}</div>
            <div className=" ps-0 col-4 text-nowrap">
              {item?.jobName?.slice(0, 30)}
              {item?.jobName?.length >= 30 ? "..." : ""}
            </div>
            <div className="ps-0 col-4">
              {truncateString(item?.customerName, 30)}
            </div>
            <div className="col-2 text-center">
              {item.WoDueDate
                ? convertSqlTimestampToDate(item.WoDueDate, false, true)
                : "-"}
            </div>
            {/* <div className="col-1 text-end">{item?.totalQuantity || "N/A"}</div> */}
          </div>
        ))}

        {!loading && WOData?.length === 0 && (
          <div className="text-center py-2 text-muted">No records found</div>
        )}
      </div>

      <div className="d-flex justify-content-between align-items-center px-2 mt-1">
        <div className="small text-muted">
          Showing {WOData?.length || 0} of {totalRecords || 0}
        </div>
        {hasMore && (
          <button
            onClick={onLoadMore}
            disabled={loading}
            className="btn btn-sm btn-outline-primary"
          >
            {loading ? "Loading..." : "Load More"}
          </button>
        )}
      </div>

      <QuickViewWorkOrder
        filterBy={productType}
        // selectedId={selectedItem?.woId}
        updateSearchParams={updateSearchParams}
        disabledInvoice={disableInvoiceReady}
        skipRefreshOnClose={true}
        show={showProductsModal}
        setShow={setShowProductsModal}
        workOrdersList={WOData}
        refreshFunc={GetJobBoardData}
      />
      {/*   View ALL Modal
       ********************************************* */}
      <Modal
        size="xl"
        show={openFullView}
        onHide={() => setOpenFullView(false)}
      >
        <Modal.Body className="scrollbar-hide ">
          <div>
            <JobBoardFullViewData
              filterBy={productType}
              WOData={WOData}
              API_CALL={API_CALL}
              filterText={filterText}
              selectedLocation={selectedLocation}
              // The Pending endpoint rejects a request without WOStatus, so
              // the full view needs the same status the card fetch sends.
              woStatus={woStatus}
              dateFrom={dateFrom}
              dateTo={dateTo}
              sortBy={sort.by}
              sortDir={sort.dir}
            />
          </div>
          <div className="text-end">
            <button
              className="btn btn-secondary"
              onClick={() => {
                setOpenFullView(false);
              }}
            >
              Close
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
